import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Cloud, Loader2, RefreshCw, ArrowUpDown, AlertCircle, Sun, Moon, Database } from "lucide-react";
import { getProxyUsage, type ProxyUsageRow, type ProxyUsageWindow } from "../../services/ProxyUsageService";

type SortKey = "totalGB" | "beforeNoonGB" | "afternoonGB" | "storeId" | "retailer" | "lastUpdated" | "totalRequests" | "totalPages";
type SortDir = "asc" | "desc";

const RETAILER_LABEL: Record<string, { label: string; icon: string }> = {
  walmart: { label: "Walmart", icon: "🏪" },
  homeDepot: { label: "Home Depot", icon: "🔨" },
  target: { label: "Target", icon: "🎯" },
  bestBuy: { label: "Best Buy", icon: "🏷️" },
  costco: { label: "Costco", icon: "🛒" },
  dollarGeneral: { label: "Dollar General", icon: "💲" },
};

const fmtNum = (n: number | undefined) => (n == null ? "—" : n.toLocaleString());
const fmtTime = (ms: number | undefined) => {
  if (!ms) return "—";
  const d = new Date(ms);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};
const fmtDuration = (startMs?: number, endMs?: number) => {
  if (!startMs || !endMs || endMs < startMs) return "—";
  const sec = Math.round((endMs - startMs) / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  if (min < 60) return `${min}m ${remSec}s`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return `${hr}h ${remMin}m`;
};

const totalGB = (row: ProxyUsageRow) => (row.beforeNoon?.proxyGB ?? 0) + (row.afternoon?.proxyGB ?? 0);
const totalRequests = (row: ProxyUsageRow) => (row.beforeNoon?.requestCount ?? 0) + (row.afternoon?.requestCount ?? 0);
const totalPages = (row: ProxyUsageRow) => (row.beforeNoon?.pages ?? 0) + (row.afternoon?.pages ?? 0);

export default function ProxyUsageTab() {
  const [sortKey, setSortKey] = useState<SortKey>("totalGB");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [filter, setFilter] = useState("");

  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ["admin-proxy-usage"],
    queryFn: getProxyUsage,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);
  const totals = data?.totals;

  const filteredSorted = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const filtered = f
      ? rows.filter(
          (r) =>
            r.storeId.toLowerCase().includes(f) ||
            r.retailer.toLowerCase().includes(f) ||
            (r.beforeNoon?.estDateYMD ?? "").toLowerCase().includes(f) ||
            (r.afternoon?.estDateYMD ?? "").toLowerCase().includes(f),
        )
      : rows;
    const sorted = [...filtered].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      switch (sortKey) {
        case "storeId":
          return a.storeId.localeCompare(b.storeId, undefined, { numeric: true }) * dir;
        case "retailer":
          return a.retailer.localeCompare(b.retailer) * dir;
        case "beforeNoonGB":
          return ((a.beforeNoon?.proxyGB ?? 0) - (b.beforeNoon?.proxyGB ?? 0)) * dir;
        case "afternoonGB":
          return ((a.afternoon?.proxyGB ?? 0) - (b.afternoon?.proxyGB ?? 0)) * dir;
        case "lastUpdated":
          return ((a.lastUpdated ?? 0) - (b.lastUpdated ?? 0)) * dir;
        case "totalRequests":
          return (totalRequests(a) - totalRequests(b)) * dir;
        case "totalPages":
          return (totalPages(a) - totalPages(b)) * dir;
        case "totalGB":
        default:
          return (totalGB(a) - totalGB(b)) * dir;
      }
    });
    return sorted;
  }, [rows, filter, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "storeId" || key === "retailer" ? "asc" : "desc");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400 dark:text-dark-400" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">Failed to load proxy usage</p>
          <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">{(error as Error)?.message ?? "Unknown error"}</p>
        </div>
        <button
          onClick={() => refetch()}
          className="text-xs font-medium text-red-700 dark:text-red-300 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-sky-100 dark:bg-sky-900/30 rounded-lg">
            <Cloud className="w-5 h-5 text-sky-600 dark:text-sky-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Proxy Usage</h2>
            <p className="text-xs text-gray-500 dark:text-dark-400">
              {dataUpdatedAt ? `Loaded ${fmtTime(dataUpdatedAt)}` : "—"} · {totals?.stores ?? 0} stores
            </p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Totals summary cards */}
      {totals && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard
            label="Total proxy"
            value={`${totals.totalGB.toFixed(2)} GB`}
            icon={<Cloud className="w-4 h-4" />}
            tone="sky"
          />
          <SummaryCard
            label="Before noon"
            value={`${totals.beforeNoonGB.toFixed(2)} GB`}
            sub={`${totals.beforeNoonPages.toLocaleString()} pages`}
            icon={<Sun className="w-4 h-4" />}
            tone="amber"
          />
          <SummaryCard
            label="Afternoon"
            value={`${totals.afternoonGB.toFixed(2)} GB`}
            sub={`${totals.afternoonPages.toLocaleString()} pages`}
            icon={<Moon className="w-4 h-4" />}
            tone="indigo"
          />
          <SummaryCard
            label="Total requests"
            value={totals.totalRequests.toLocaleString()}
            sub={`${totals.totalItems.toLocaleString()} items`}
            icon={<Database className="w-4 h-4" />}
            tone="emerald"
          />
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by store ID, retailer, or date…"
          className="flex-1 px-3 py-2 text-sm bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-gray-400 dark:placeholder:text-dark-500 text-gray-900 dark:text-white"
        />
        <span className="text-xs text-gray-500 dark:text-dark-400 whitespace-nowrap">
          {filteredSorted.length} / {rows.length}
        </span>
      </div>

      {/* Table - desktop */}
      <div className="hidden md:block bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-dark-900/50 border-b border-gray-200 dark:border-dark-700">
                <Th onClick={() => toggleSort("retailer")} active={sortKey === "retailer"} dir={sortDir}>Retailer</Th>
                <Th onClick={() => toggleSort("storeId")} active={sortKey === "storeId"} dir={sortDir}>Store</Th>
                <Th onClick={() => toggleSort("beforeNoonGB")} active={sortKey === "beforeNoonGB"} dir={sortDir} className="text-right">AM</Th>
                <Th onClick={() => toggleSort("afternoonGB")} active={sortKey === "afternoonGB"} dir={sortDir} className="text-right">PM</Th>
                <Th onClick={() => toggleSort("totalGB")} active={sortKey === "totalGB"} dir={sortDir} className="text-right">Total</Th>
                <Th onClick={() => toggleSort("totalPages")} active={sortKey === "totalPages"} dir={sortDir} className="text-right">Pages</Th>
                <Th onClick={() => toggleSort("totalRequests")} active={sortKey === "totalRequests"} dir={sortDir} className="text-right">Requests</Th>
                <Th onClick={() => toggleSort("lastUpdated")} active={sortKey === "lastUpdated"} dir={sortDir} className="text-right">Last updated</Th>
              </tr>
            </thead>
            <tbody>
              {filteredSorted.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-sm text-gray-500 dark:text-dark-400">
                    No proxy usage rows found.
                  </td>
                </tr>
              ) : (
                filteredSorted.map((row) => {
                  const meta = RETAILER_LABEL[row.retailer] ?? { label: row.retailer, icon: "📦" };
                  const total = totalGB(row);
                  return (
                    <tr
                      key={`${row.retailer}#${row.storeId}`}
                      className="border-b border-gray-100 dark:border-dark-700/60 last:border-0 hover:bg-gray-50 dark:hover:bg-dark-700/30 transition-colors"
                    >
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1.5 text-gray-800 dark:text-dark-200">
                          <span>{meta.icon}</span>
                          <span className="font-medium">{meta.label}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-gray-700 dark:text-dark-300">{row.storeId}</td>
                      <WindowCell window={row.beforeNoon} accent="amber" />
                      <WindowCell window={row.afternoon} accent="indigo" />
                      <td className="px-3 py-2.5 text-right">
                        <span className={`font-semibold ${total > 1 ? "text-rose-600 dark:text-rose-400" : "text-gray-900 dark:text-white"}`}>
                          {total.toFixed(2)} GB
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right text-gray-700 dark:text-dark-300">{fmtNum(totalPages(row))}</td>
                      <td className="px-3 py-2.5 text-right text-gray-700 dark:text-dark-300">{fmtNum(totalRequests(row))}</td>
                      <td className="px-3 py-2.5 text-right text-xs text-gray-500 dark:text-dark-400">{fmtTime(row.lastUpdated)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cards - mobile */}
      <div className="md:hidden space-y-3">
        {filteredSorted.length === 0 ? (
          <div className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700 p-6 text-center text-sm text-gray-500 dark:text-dark-400">
            No proxy usage rows found.
          </div>
        ) : (
          filteredSorted.map((row) => {
            const meta = RETAILER_LABEL[row.retailer] ?? { label: row.retailer, icon: "📦" };
            const total = totalGB(row);
            return (
              <div
                key={`${row.retailer}#${row.storeId}`}
                className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700 p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-base">{meta.icon}</span>
                    <span className="font-semibold text-sm text-gray-900 dark:text-white">{meta.label}</span>
                    <span className="font-mono text-xs text-gray-500 dark:text-dark-400">#{row.storeId}</span>
                  </div>
                  <span className={`text-sm font-semibold ${total > 1 ? "text-rose-600 dark:text-rose-400" : "text-gray-900 dark:text-white"}`}>
                    {total.toFixed(2)} GB
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <WindowMobile title="AM" icon={<Sun className="w-3.5 h-3.5" />} window={row.beforeNoon} accent="amber" />
                  <WindowMobile title="PM" icon={<Moon className="w-3.5 h-3.5" />} window={row.afternoon} accent="indigo" />
                </div>
                <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-dark-400 pt-1 border-t border-gray-100 dark:border-dark-700">
                  <span>{fmtNum(totalPages(row))} pages · {fmtNum(totalRequests(row))} reqs</span>
                  <span>Updated {fmtTime(row.lastUpdated)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Th({
  children,
  onClick,
  active,
  dir,
  className = "",
}: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  dir: SortDir;
  className?: string;
}) {
  return (
    <th className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-400 text-left ${className}`}>
      <button
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors ${active ? "text-gray-900 dark:text-white" : ""}`}
      >
        {children}
        <ArrowUpDown className={`w-3 h-3 ${active ? "opacity-100" : "opacity-30"} ${active && dir === "asc" ? "rotate-180" : ""}`} />
      </button>
    </th>
  );
}

function WindowCell({ window, accent }: { window?: ProxyUsageWindow; accent: "amber" | "indigo" }) {
  if (!window) {
    return <td className="px-3 py-2.5 text-right text-xs text-gray-300 dark:text-dark-600">—</td>;
  }
  const accentText =
    accent === "amber"
      ? "text-amber-700 dark:text-amber-300"
      : "text-indigo-700 dark:text-indigo-300";
  return (
    <td className="px-3 py-2.5 text-right">
      <div className={`text-sm font-semibold ${accentText}`}>{window.proxyGB.toFixed(2)} GB</div>
      <div className="text-[10px] text-gray-500 dark:text-dark-400">
        {fmtNum(window.pages)}p · {fmtDuration(window.startedAtMs, window.endedAtMs)}
      </div>
    </td>
  );
}

function WindowMobile({
  title,
  icon,
  window,
  accent,
}: {
  title: string;
  icon: React.ReactNode;
  window?: ProxyUsageWindow;
  accent: "amber" | "indigo";
}) {
  const tone =
    accent === "amber"
      ? "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-100 dark:border-amber-900/40"
      : "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-100 dark:border-indigo-900/40";
  return (
    <div className={`rounded-lg border p-2.5 ${tone}`}>
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider opacity-80">
        {icon}
        {title}
      </div>
      {window ? (
        <>
          <div className="text-sm font-semibold mt-0.5">{window.proxyGB.toFixed(2)} GB</div>
          <div className="text-[10px] opacity-70 mt-0.5">
            {fmtNum(window.pages)}p · {fmtNum(window.itemsSeen)}i
          </div>
          <div className="text-[10px] opacity-70">{fmtDuration(window.startedAtMs, window.endedAtMs)}</div>
          {window.estDateYMD && (
            <div className="text-[10px] opacity-60 mt-0.5">{window.estDateYMD}</div>
          )}
        </>
      ) : (
        <div className="text-xs opacity-50 mt-1">No data</div>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  icon,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  tone: "sky" | "amber" | "indigo" | "emerald";
}) {
  const toneMap = {
    sky: "bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300 border-sky-100 dark:border-sky-900/30",
    amber: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-100 dark:border-amber-900/30",
    indigo: "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-100 dark:border-indigo-900/30",
    emerald: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-100 dark:border-emerald-900/30",
  } as const;
  return (
    <div className={`rounded-xl border p-3 ${toneMap[tone]}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider opacity-80">
        {icon}
        {label}
      </div>
      <div className="text-lg font-bold mt-1">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}
